import zod from 'zod'

import {
  convertToUTC,
  normalizeDate,
  validateDate,
} from '~/utils/normalizeDate'

import { pagarmeInterval, PagarmePaymentMethodEnum } from './variables'

export const planItemSchema = zod.object({
  name: zod.string().min(3, 'Deve ter ao menos 3 letras'),
  quantity: zod.number().min(1, 'Deve ser ao menos uma unidade'),
  pricing_scheme: zod.object({
    price: zod
      .number({ errorMap: () => ({ message: 'Obrigatório' }) })
      .min(0, 'Não pode ser negativo')
      .transform((v) => v * 100),
  }),
})

export const createPlanSchema = zod.object({
  name: zod.string().min(3, 'Deve ter ao menos 3 letras').trim(),
  paymentMethods: zod
    .enum(PagarmePaymentMethodEnum)
    .array()
    .min(1, 'Selecione ao menos um valor'),
  interval: zod.enum(pagarmeInterval, {
    errorMap: () => ({ message: 'Selecione um valor' }),
  }),
  intervalCount: zod
    .number({ errorMap: () => ({ message: 'Obrigatório' }) })
    .min(1, 'Valor não pode ser inferior a 1'),
  items: zod.array(planItemSchema).min(1, 'Deve haver ao menos 1 item'),
  licensesAmount: zod
    .number({ errorMap: () => ({ message: 'Obrigatório' }) })
    .min(1, 'Dever ser maior que zero'),
  installments: zod
    .number({ errorMap: () => ({ message: 'Obrigatório' }) })
    .min(1, 'Mínimo de 1 parcela')
    .max(12, 'Máximo de 12 parcelas')
    .default(1),
  visible: zod.boolean().default(true),
}).superRefine((data, ctx) => {
  if (data.interval === 'day' && data.intervalCount < 3) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      path: ['intervalCount'],
      message: 'Mínimo de 3 dias',
    })
  }
})

// export const planItemSchema = zod.object({
//   price: zod
//     .number()
//     .min(0, 'Não pode ser negativo')
//     .transform((v) => v * 100),
// })

// export const createPlanSchema = zod.object({
//   name: zod.string().min(3, 'Deve ter ao menos 3 letras'),
//   licensesAmount: zod
//     .number({ required_error: 'Deve ser maior que zero' })
//     .min(1, 'Dever ser maior que zero'),
//   paymentMethods: zod
//     .enum(PagarmePaymentMethodEnum)
//     .array()
//     .min(1, 'Selecione ao menos um valor'),
//   plans: zod.object(
//     {
//       annually: planItemSchema.optional(),
//       quarterly: planItemSchema.optional(),
//       monthly: planItemSchema.optional(),
//     },
//     { required_error: 'Insira ao menos um plano de pagamento' },
//   ),
//   visible: zod.boolean(),
//   code: zod.string().optional(),
// })

export const patchPlanSchema = zod.object({
  visible: zod.boolean(),
  code: zod.string().optional(),
})

export const updatePaymentCardSchema = zod.object({
  number: zod
    .string({ required_error: 'Obrigatório' })
    .min(18, 'Deve ter no mínimo 15 números')
    .max(19, 'Deve ter ao máximo 16 números')
    .transform((v) => v.replaceAll(/\D/g, '')),
  holder_name: zod
    .string({ required_error: 'Obrigarório' })
    .min(3, 'Obrigatório'),
  exp_month: zod
    .number({ required_error: 'Obrigatório' })
    .min(1, 'valor inválido')
    .max(12, 'Valor inválido'),
  exp_year: zod
    .number()
    .min(new Date().getFullYear() % 2000, 'Valor incorreto'),
  cvv: zod
    .string({ required_error: 'Obrigatório' })
    .min(3, 'Deve ter no mínimo 3 números')
    .max(4, 'Deve ter no máximo 4 números'),
  billing_address: zod.object({
    line_1: zod.string({ required_error: 'Obrigatório' }).min(3, 'Obrigarório'),
    line_2: zod.string({ required_error: 'Obrigatório' }).optional().nullable(),
    zip_code: zod
      .string()
      .min(9, 'Deve ter no mínimo 8 números')
      .transform((v) => v.replaceAll(/\D/g, '')),
    city: zod.string({ required_error: 'Obrigatório' }).min(3, 'Obrigarório'),
    state: zod
      .string({ required_error: 'Obrigatório' })
      .max(2, 'RJ, BA, SP')
      .toUpperCase(),
    country: zod.string().max(2).toUpperCase(),
  }),
})

export const createDiscountSchema = zod
  .object({
    planId: zod.string({ required_error: 'Obrigatório' }).uuid().optional(),
    code: zod
      .string({ required_error: 'Obrigarório' })
      .min(3, 'Deve ter ao menos 3 letras'),
    type: zod.enum(['percentage', 'flat'], { required_error: 'Obrigatório' }),
    limit: zod
      .number()
      .min(0, 'Deve ser maior ou igual a 0')
      .optional()
      .nullable(),
    private: zod.boolean().optional().nullable(),
    cycles: zod
      .number({ required_error: 'Obrigatório' })
      .min(0, 'Deve ser maior ou igual a 0')
      .optional()
      .nullable(),
    value: zod
      .number({ required_error: 'Obrigarório' })
      .min(0, 'Deve ser maior que zero'),
    expiresAt: zod
      .string({ required_error: 'Obrigatório' })
      .superRefine((val, ctx) => {
        const normalizedDate = normalizeDate(val)
        if (!validateDate(normalizedDate)) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: 'Formato de data inválido',
          })
        }
        const utcDate = convertToUTC(normalizedDate)
        if (utcDate < new Date()) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: 'Não pode ser uma data passada',
          })
        }
      })
      .transform((val) => convertToUTC(normalizeDate(val)).toISOString()),
  })
  .transform((data) => {
    if (data.type === 'flat') {
      data.value = data.value * 100
    }

    if (data.cycles === null || data.cycles === undefined || data.cycles <= 0) {
      data.cycles = undefined
    }
    if (data.limit === null || data.limit === undefined || data.limit <= 0) {
      data.limit = undefined
    }

    return data
  })

export type PlanItem = zod.infer<typeof planItemSchema>
export type CreatePlanDTO = zod.infer<typeof createPlanSchema>
export type PatchPlanDTO = zod.infer<typeof patchPlanSchema>
export type UpdatePaymentCardDTO = zod.infer<typeof updatePaymentCardSchema>
export type CreateDiscountDTO = zod.infer<typeof createDiscountSchema>
